import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileSpreadsheet, FileJson, Loader2 } from "lucide-react";
import { downloadFile } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { RestoreCard } from "@/components/RestoreCard";
import { SafetyBackupCard } from "@/components/SafetyBackupCard";

const FORMATS = [
  { key: "excel", label: "Excel Yedeği İndir", icon: FileSpreadsheet, ext: "xlsx",
    hint: "Her tablo ayrı bir sayfada; Excel'de açılıp okunabilir." },
  { key: "json", label: "JSON Yedeği İndir", icon: FileJson, ext: "json",
    hint: "Tüm kayıtlar eksiksiz, makinece okunabilir tek dosya." },
];

export default function BackupPage() {
  const [busy, setBusy] = useState("");
  const [summary, setSummary] = useState(null);
  const { isAdmin } = useAuth();
  const [safetyKey, setSafetyKey] = useState(0);
  const [revertKey, setRevertKey] = useState(0);
  const afterRestore = () => { loadSummary(); setSafetyKey((k) => k + 1); };

  const loadSummary = () => api.get("/backup/summary").then((r) => setSummary(r.data)).catch(() => setSummary([]));
  useEffect(() => { loadSummary(); }, []);

  const download = async (f) => {
    setBusy(f.key);
    try {
      await downloadFile(`/backup/${f.key}`, `khgb_yedek_${new Date().toISOString().slice(0, 10)}.${f.ext}`);
      toast.success("Yedek indirildi");
    } catch (e) {
      toast.error("Yedek alınamadı");
    } finally {
      setBusy("");
    }
  };

  return (
    <AppLayout title="Yedekleme" subtitle="Tüm verileri tek dosya olarak bilgisayarınıza indirin">
      <Card className="p-6 border border-slate-200 shadow-sm max-w-3xl" data-testid="backup-page-card">
        <h3 className="font-display font-semibold text-slate-900">Tam Veri Yedeği</h3>
        <p className="text-sm text-slate-500 mt-1">
          Köyler, çalışma kayıtları, malzemeler ve malzeme girişleri, dilekçeler, altyapı projeleri ve yatırımlar
          (kategori ve kurum listeleriyle birlikte) tek bir dosyaya indirilir. Biçimi seçin.
          Şifreler ve yüklenen dosyalar (dilekçe ekleri, logo) yedeğe dahil değildir.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          {FORMATS.map((f) => (
            <div key={f.key} className="rounded-md border border-slate-200 p-4 flex flex-col gap-3">
              <div className="text-sm text-slate-600">{f.hint}</div>
              <Button onClick={() => download(f)} disabled={!!busy} className="bg-emerald-700 hover:bg-emerald-800 mt-auto"
                      data-testid={`backup-download-${f.key}-button`}>
                {busy === f.key ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <f.icon className="h-4 w-4 mr-1.5" />}
                {f.label}
              </Button>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-6 border border-slate-200 shadow-sm max-w-3xl mt-6" data-testid="backup-summary-card">
        <h3 className="font-display font-semibold text-slate-900">Yedeğe Girecek Kayıtlar</h3>
        {!summary ? <div className="text-sm text-slate-500 mt-3">Yükleniyor...</div> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
            {summary.map((s) => (
              <div key={s.key} className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2" data-testid={`backup-count-${s.key}`}>
                <div className="text-xs text-slate-500">{s.title}</div>
                <div className="text-lg font-semibold text-slate-900">{s.count}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {isAdmin && <RestoreCard onDone={afterRestore} resetKey={revertKey} />}
      {isAdmin && <SafetyBackupCard refreshKey={safetyKey} onDone={() => { loadSummary(); setRevertKey((k) => k + 1); }} />}
    </AppLayout>
  );
}
